// Business-date helpers — the frontend mirror of backend/timeutil.py. Read
// that file's docstring first; the reasoning is identical and the two must
// agree, because dates written here are read back by the billing code there.
//
// The 3PL operates in Australia. `new Date().toISOString().slice(0, 10)`
// yields the UTC calendar date, which is YESTERDAY in Sydney between midnight
// and 10am (11am once AEDT starts). That is what stamped a manual warehouse
// entry made on the morning of 28/09 as 27/09, while the Slack ingest — which
// converts to Sydney at backend/routers/warehouse.py:779 — correctly recorded
// 28/09 for the same day's work.
//
// Browser-local time would usually be right for Australian staff, but only by
// coincidence: it breaks silently for anyone working from another timezone and
// it is not what the backend uses. Resolving Australia/Sydney explicitly makes
// the manual and Slack paths agree by construction rather than by luck.
//
// USE these for any date STORED as a business fact or used as a billing-period
// default — activity_date, started_on/ended_on, ship_date, label_created_at,
// received_date, and report range defaults.
//
// DO NOT use them for audit timestamps (created_at, synced_at, updated_at).
// Those stay UTC, exactly as timeutil.py says.

// A NAMED zone, not a fixed offset, so boundaries stay correct across the
// AEST/AEDT transitions (AEDT begins the first Sunday in October).
export const BUSINESS_TZ = 'Australia/Sydney'

// Built once — constructing an Intl.DateTimeFormat is expensive and these are
// called from render paths. 'en-CA' is the locale trick that yields an
// ISO-shaped YYYY-MM-DD, which is what <input type="date"> and the API expect.
const _isoDateFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TZ,
  year:  'numeric',
  month: '2-digit',
  day:   '2-digit',
})

/** Today's calendar date in the business timezone, as 'YYYY-MM-DD'. */
export function businessToday() {
  return _isoDateFmt.format(new Date())
}

/** First day of the current business month, as 'YYYY-MM-DD'. */
export function businessMonthStart() {
  return `${businessToday().slice(0, 7)}-01`
}

/**
 * Shift a 'YYYY-MM-DD' string by n days (negative to go back).
 *
 * Pure calendar arithmetic, done entirely in UTC so it can never pick up a
 * timezone shift. The naive version — new Date(iso + 'T00:00:00') to parse at
 * LOCAL midnight, mutate, then .toISOString() to serialise through UTC —
 * silently cancels its own offset in any UTC+ zone, which is how
 * StorageTab's "day after the entry started" guard ended up returning the
 * start date itself.
 */
export function addDays(iso, n) {
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y || !m || !d) return iso
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}

// ─── Month / quarter / financial-year helpers (Profit & Loss) ───────────────
// Pure 'YYYY-MM-DD' string arithmetic in UTC, like addDays above. Periods
// follow the Australian financial year: July–June, Q1 = Jul–Sep, and the FY is
// named for the calendar year it ENDS in (FY27 = Jul 2026 – Jun 2027).

const _MONTHS_LONG  = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const _MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function _ym(iso) {
  const [y, m] = String(iso).split('-').map(Number)
  return [y, m]
}
function _iso(y, m, d) {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** First day of the month containing iso. */
export function monthStart(iso) {
  const [y, m] = _ym(iso)
  return _iso(y, m, 1)
}

/** Last day of the month containing iso. */
export function monthEnd(iso) {
  const [y, m] = _ym(iso)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

/** First day of the month n months after the month containing iso. */
export function addMonths(iso, n) {
  const [y, m] = _ym(iso)
  const idx = y * 12 + (m - 1) + n
  return _iso(Math.floor(idx / 12), (idx % 12) + 1, 1)
}

/** 'October 2026' (or 'Oct 2026' when short). */
export function monthLabel(iso, short = false) {
  const [y, m] = _ym(iso)
  return `${(short ? _MONTHS_SHORT : _MONTHS_LONG)[m - 1]} ${y}`
}

/** The AU financial year a date falls in, named by its ending year (2027 for Oct 2026). */
export function fyOf(iso) {
  const [y, m] = _ym(iso)
  return m >= 7 ? y + 1 : y
}

/** 'FY27' for any date between 1 Jul 2026 and 30 Jun 2027. */
export function fyLabel(iso) {
  return `FY${String(fyOf(iso) % 100).padStart(2, '0')}`
}

/** First and last day of the AU financial year containing iso. */
export function fyRange(iso) {
  const fy = fyOf(iso)
  return { from: _iso(fy - 1, 7, 1), to: _iso(fy, 6, 30) }
}

/** FY quarter (1–4) containing iso: Q1 = Jul–Sep … Q4 = Apr–Jun. */
export function fyQuarterOf(iso) {
  const [, m] = _ym(iso)
  return Math.floor(((m + 5) % 12) / 3) + 1
}

/** First and last day of the FY quarter containing iso. */
export function fyQuarterRange(iso) {
  const [y, m] = _ym(iso)
  const startMonthIdx = (m - 1) - (((m + 5) % 12) % 3)   // 0-based month the quarter starts in
  const from = _iso(y, startMonthIdx + 1, 1)
  return { from, to: monthEnd(addMonths(from, 2)) }
}

/**
 * Period window for a P&L granularity ('day' and 'month' both mean the month).
 * Returns { from, to, label }.
 */
export function periodRange(granularity, iso) {
  if (granularity === 'quarter') {
    const r = fyQuarterRange(iso)
    return {
      ...r,
      label: `Q${fyQuarterOf(iso)} ${fyLabel(iso)} (${_MONTHS_SHORT[_ym(r.from)[1] - 1]}–${monthLabel(r.to, true)})`,
    }
  }
  if (granularity === 'year') {
    const r = fyRange(iso)
    return { ...r, label: `${fyLabel(iso)} (${monthLabel(r.from, true)} – ${monthLabel(r.to, true)})` }
  }
  const from = monthStart(iso)
  return {
    from,
    to: monthEnd(iso),
    label: granularity === 'day' ? `${monthLabel(iso)}, by day` : monthLabel(iso),
  }
}

/** Step a period anchor one period back (dir = -1) or forward (+1); returns a month start. */
export function shiftPeriod(granularity, iso, dir) {
  const months = granularity === 'quarter' ? 3 : granularity === 'year' ? 12 : 1
  return addMonths(iso, months * dir)
}
